import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Settings, TrendingUp, UserMinus, Users, AlertCircle } from "lucide-react";

const DETAILED_GRADES = ["PF", "PRE2", "JK-A", "JK-B", "SK-A", "SK-B", "G1-A", "G1-B", "G2-A", "G2-B", "G3-A", "G3-B", "G4-A", "G4-B", "G5-A", "G5-B", "G6", "G7-A", "G7-B", "G8"];

const GradeManagement = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [stats, setStats] = useState([]);
  const [withdrawnStudents, setWithdrawnStudents] = useState([]);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityGrade, setCapacityGrade] = useState("");
  const [capacityValue, setCapacityValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("capacity");

  useEffect(() => {
    fetchStats();
    fetchWithdrawnStudents();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/waitlist`);
      setStats(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch statistics");
      setStats([]);
    }
  };

  const fetchWithdrawnStudents = async () => {
    try {
      const response = await axios.get(`${API}/admin/withdrawn-students`);
      setWithdrawnStudents(response.data || []);
    } catch (error) {
      toast.error("Failed to fetch withdrawn students");
      setWithdrawnStudents([]);
    }
  };

  const handleSetCapacity = async () => {
    if (!capacityGrade || !capacityValue) {
      toast.error("Please select a grade and enter capacity");
      return;
    }

    try {
      await axios.put(`${API}/admin/grade-capacity`, {
        grade: capacityGrade,
        capacity: parseInt(capacityValue)
      });
      toast.success("Capacity updated successfully");
      setShowCapacityModal(false);
      setCapacityGrade("");
      setCapacityValue("");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update capacity");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin-login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/admin/dashboard")}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="border-l border-[#0E366A]/30 pl-4">
              <h1 className="text-2xl font-bold text-[#0E366A] font-serif">Grade Management</h1>
              <p className="text-sm text-[#0E366A] italic">Configure grade capacities</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout}
            className="border-[#0E366A] text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white border-2 border-[#F7B538]">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Grades</p>
                  <p className="text-3xl font-bold text-[#0E366A]">{stats?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#F7B538]/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#0E366A]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Enrolled</p>
                  <p className="text-3xl font-bold text-green-600">
                    {stats.reduce((sum, s) => sum + s.current_enrolled, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Waiting</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats.reduce((sum, s) => sum + s.total_waiting, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Withdrawn</p>
                  <p className="text-3xl font-bold text-red-600">{withdrawnStudents?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserMinus className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex items-center justify-between">
            <TabsList className="bg-white border border-[#F7B538]">
              <TabsTrigger 
                value="overview" 
                className="data-[state=active]:bg-[#F7B538] data-[state=active]:text-[#0E366A]"
              >
                <Users className="w-4 h-4 mr-2" />
                Grades Overview
              </TabsTrigger>
              <TabsTrigger 
                value="withdrawn"
                className="data-[state=active]:bg-[#F7B538] data-[state=active]:text-[#0E366A]"
              >
                <UserMinus className="w-4 h-4 mr-2" />
                Withdrawn Students ({withdrawnStudents?.length || 0})
              </TabsTrigger>
            </TabsList>

            <Button 
              onClick={() => setShowCapacityModal(true)}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              <Settings className="w-4 h-4 mr-2" />
              Set Capacity
            </Button>
          </div>

          {/* Grades Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <Card className="bg-white">
              <CardHeader className="bg-[#F7B538]/10 border-b border-[#F7B538]">
                <CardTitle className="text-[#0E366A]">All Grades Overview</CardTitle>
                <CardDescription>Enrollment statistics and capacity management</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#F7B538]/10">
                        <TableHead className="text-[#0E366A] font-semibold">Grade</TableHead>
                        <TableHead className="text-right text-[#0E366A] font-semibold">Enrolled</TableHead>
                        <TableHead className="text-right text-[#0E366A] font-semibold">Capacity</TableHead>
                        <TableHead className="text-right text-[#0E366A] font-semibold">Waiting</TableHead>
                        <TableHead className="text-right text-[#0E366A] font-semibold">Available</TableHead>
                        <TableHead className="text-right text-[#0E366A] font-semibold">Utilization</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stats && stats.map((stat) => {
                        const utilization = stat.capacity > 0 
                          ? Math.round((stat.current_enrolled / stat.capacity) * 100)
                          : 0;
                    
                    return (
                      <TableRow key={stat.grade}>
                        <TableCell className="font-semibold text-[#0E366A]">{stat.grade}</TableCell>
                        <TableCell className="text-right text-gray-700">{stat.current_enrolled}</TableCell>
                        <TableCell className="text-right text-gray-700">{stat.capacity}</TableCell>
                        <TableCell className="text-right text-gray-700">{stat.total_waiting}</TableCell>
                        <TableCell className="text-right">
                          <span className={stat.available_spots > 0 ? "text-green-600 font-semibold" : "text-gray-500"}>
                            {stat.available_spots}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  utilization >= 100 ? 'bg-red-500' :
                                  utilization >= 90 ? 'bg-yellow-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(utilization, 100)}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-12">{utilization}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      {/* Withdrawn Students Tab */}
      <TabsContent value="withdrawn" className="space-y-4">
        <Card className="bg-white">
          <CardHeader className="bg-red-50 border-b border-red-200">
            <CardTitle className="text-red-600 flex items-center">
              <UserMinus className="w-5 h-5 mr-2" />
              Withdrawn Students
            </CardTitle>
            <CardDescription>Students who have been withdrawn from enrollment</CardDescription>
          </CardHeader>
          <CardContent>
            {!withdrawnStudents || withdrawnStudents.length === 0 ? (
              <div className="text-center py-12">
                <UserMinus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No withdrawn students</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-red-50">
                      <TableHead className="text-red-600 font-semibold">#</TableHead>
                      <TableHead className="text-red-600 font-semibold">Application Code</TableHead>
                      <TableHead className="text-red-600 font-semibold">Student Name</TableHead>
                      <TableHead className="text-red-600 font-semibold">Grade</TableHead>
                      <TableHead className="text-red-600 font-semibold">Parent Name</TableHead>
                      <TableHead className="text-red-600 font-semibold">Parent Email</TableHead>
                      <TableHead className="text-red-600 font-semibold">Withdrawn Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {withdrawnStudents && withdrawnStudents.map((student, idx) => (
                      <TableRow key={idx} className="hover:bg-red-50">
                        <TableCell className="font-medium text-gray-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium text-gray-900">{student.application_code}</TableCell>
                        <TableCell className="text-gray-900">
                          {student.first_name} {student.last_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-red-300 text-red-600">
                            {student.grade}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-700">{student.parent_name}</TableCell>
                        <TableCell className="text-gray-700">{student.parent_email}</TableCell>
                        <TableCell className="text-gray-600">{student.withdrawn_date}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
      </div>

      {/* Capacity Modal */}
      <Dialog open={showCapacityModal} onOpenChange={setShowCapacityModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Grade Capacity</DialogTitle>
            <DialogDescription>Update the enrollment capacity for a specific grade</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Grade</Label>
              <Select value={capacityGrade} onValueChange={setCapacityGrade}>
                <SelectTrigger>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {DETAILED_GRADES.map(grade => (
                    <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Capacity</Label>
              <Input 
                type="number"
                min="0"
                value={capacityValue}
                onChange={(e) => setCapacityValue(e.target.value)}
                placeholder="Enter capacity number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCapacityModal(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSetCapacity}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GradeManagement;
